import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ColDef, ICellRendererParams} from '@ag-grid-community/core';
import {AgGridReact} from '@ag-grid-community/react';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';

import {
    API_CHECK_AUTH_URL,
    API_SETTINGS_ACCESS_OPTIONS,
    API_SETTINGS_ACCESS_PROFILES,
    API_SETTINGS_ACCESS_USERS,
} from '../../service/api.endpoint';
import http from '../../service/axiosConfig';
import * as S from './page.adminSettings.styled';

type RefItem = {
    id: string;
    name: string;
    label?: string;
    deposit?: string;
};

type UserOption = {
    id: string;
    username: string;
    name: string;
};

type RoleOption = {
    id: string;
    name: string;
};

type ModuleOption = {
    id: string;
    name: string;
};

type AccessProfile = {
    id: string;
    user: string;
    user_display: string;
    username: string;
    role: string;
    role_display: string;
    orgs: string[];
    org_names: string[];
    deposits: string[];
    deposit_names: string[];
    dns: string[];
    dns_names: string[];
    enabled_modules: string[];
    effective_modules: string[];
    default_modules: string[];
    can_upload_graph: boolean;
    can_manage_access: boolean;
    is_active: boolean;
    comment: string;
};

type OptionsPayload = {
    roles: RoleOption[];
    users: UserOption[];
    orgs: RefItem[];
    deposits: RefItem[];
    dns: RefItem[];
    modules: ModuleOption[];
    role_default_modules: Record<string, string[]>;
};

type AccountRow = {
    id: string;
    user: string;
    username: string;
    user_display: string;
    role_display: string;
    org_names: string[];
    deposit_names: string[];
    dns_names: string[];
    module_names: string;
    rights: string;
    status: string;
    status_kind: 'active' | 'inactive' | 'empty';
    profile?: AccessProfile;
};

const emptyOptions: OptionsPayload = {
    roles: [],
    users: [],
    orgs: [],
    deposits: [],
    dns: [],
    modules: [],
    role_default_modules: {},
};

const emptyForm = {
    user: '',
    role: 'CONTRACTOR',
    orgs: [] as string[],
    deposits: [] as string[],
    dns: [] as string[],
    enabled_modules: [] as string[],
    can_upload_graph: false,
    can_manage_access: false,
    comment: '',
};

const emptyUserForm = {
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email: '',
};

const LOCATION_SCOPE_ROLES = ['MASTER_DNS', 'OSF', 'CLUSTER'];

const hasAccessManagement = (access: any) => {
    if (access?.role === 'CONTRACTOR') {
        return false;
    }
    return Boolean(access?.is_admin) || access?.role === 'ADMIN' || Boolean(access?.can_manage_access);
};

const isContractorRole = (role: string) => role === 'CONTRACTOR';

const usesLocationScope = (role: string) => LOCATION_SCOPE_ROLES.includes(role);

const getAllowedModulesForRole = (role: string, options: OptionsPayload) => (
    options.role_default_modules[role] || []
);

const sanitizeModulesForRole = (role: string, modules: string[], options: OptionsPayload) => {
    const allowedModules = getAllowedModulesForRole(role, options);
    return modules.filter((moduleId) => allowedModules.includes(moduleId));
};

const sanitizeAccessFormForRole = (formValue: typeof emptyForm, options: OptionsPayload) => ({
    ...formValue,
    orgs: isContractorRole(formValue.role) ? formValue.orgs : [],
    deposits: usesLocationScope(formValue.role) ? formValue.deposits : [],
    dns: usesLocationScope(formValue.role) ? formValue.dns : [],
    can_manage_access: isContractorRole(formValue.role) ? false : formValue.can_manage_access,
    enabled_modules: sanitizeModulesForRole(
        formValue.role,
        formValue.enabled_modules.length
            ? formValue.enabled_modules
            : getAllowedModulesForRole(formValue.role, options),
        options,
    ),
});

function PageAdminSettings() {
    const [options, setOptions] = useState<OptionsPayload>(emptyOptions);
    const [profiles, setProfiles] = useState<AccessProfile[]>([]);
    const [form, setForm] = useState(emptyForm);
    const [userForm, setUserForm] = useState(emptyUserForm);
    const [editingProfile, setEditingProfile] = useState<AccessProfile | null>(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [editorTab, setEditorTab] = useState<'access' | 'modules' | 'requests'>('access');
    const [quickSearch, setQuickSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'empty'>('all');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const canOpenFromStorage = useMemo(() => {
        try {
            const raw = localStorage.getItem('access');
            const access = raw ? JSON.parse(raw) : {};
            return hasAccessManagement(access);
        } catch {
            return false;
        }
    }, []);
    const [canOpen, setCanOpen] = useState(canOpenFromStorage);

    const getProfileModules = useCallback((profile: AccessProfile | undefined) => {
        if (!profile) return [];
        if (profile.enabled_modules?.length) return sanitizeModulesForRole(profile.role, profile.enabled_modules, options);
        if (profile.effective_modules?.length) return sanitizeModulesForRole(profile.role, profile.effective_modules, options);
        return options.role_default_modules[profile.role] || [];
    }, [options]);

    const selectedAccessProfile = useMemo(
        () => editingProfile || profiles.find((item) => item.user === form.user),
        [editingProfile, form.user, profiles],
    );

    const accessStats = useMemo(() => {
        const activeProfiles = profiles.filter((item) => item.is_active);
        return {
            users: options.users.length,
            profiles: activeProfiles.length,
            admins: activeProfiles.filter((item) => item.role === 'ADMIN').length,
            contractors: activeProfiles.filter((item) => item.role === 'CONTRACTOR').length,
            scoped: activeProfiles.filter((item) => item.role !== 'ADMIN' && item.role !== 'CONTRACTOR').length,
        };
    }, [options.users.length, profiles]);

    const accountRows = useMemo<AccountRow[]>(() => {
        const profileByUser = new Map(profiles.map((profile) => [profile.user, profile]));
        return options.users.map((user) => {
            const profile = profileByUser.get(user.id);
            const rights = [
                profile?.can_upload_graph ? 'График' : '',
                profile?.can_manage_access ? 'Доступы' : '',
            ].filter(Boolean).join(', ');

            return {
                id: profile?.id || `user-${user.id}`,
                user: user.id,
                username: user.username,
                user_display: profile?.user_display || user.name || user.username,
                role_display: profile?.role_display || 'Без профиля',
                org_names: profile?.org_names || [],
                deposit_names: profile?.deposit_names || [],
                dns_names: profile?.dns_names || [],
                module_names: profile ? formatModuleList(profile.effective_modules, options.modules) : '—',
                rights: rights || '—',
                status: profile ? (profile.is_active ? 'Активен' : 'Отключен') : 'Без профиля',
                status_kind: profile ? (profile.is_active ? 'active' : 'inactive') : 'empty',
                profile,
            };
        });
    }, [options.modules, options.users, profiles]);

    const filteredRows = useMemo(() => accountRows.filter((row) => {
        const matchesRole = !roleFilter || row.profile?.role === roleFilter;
        const matchesStatus = statusFilter === 'all' || row.status_kind === statusFilter;
        return matchesRole && matchesStatus;
    }), [accountRows, roleFilter, statusFilter]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const userResponse = await http.get(API_CHECK_AUTH_URL);
            const access = userResponse.data?.data?.access || {};
            localStorage.setItem('access', JSON.stringify(access));
            if (!hasAccessManagement(access)) {
                setCanOpen(false);
                setMessage({type: 'error', text: 'Настройки доступа недоступны текущему пользователю'});
                return;
            }
            setCanOpen(true);

            const [optionsResponse, profilesResponse] = await Promise.all([
                http.get(API_SETTINGS_ACCESS_OPTIONS),
                http.get(API_SETTINGS_ACCESS_PROFILES),
            ]);
            setOptions({...emptyOptions, ...(optionsResponse.data || {})});
            setProfiles(profilesResponse.data?.results || []);
        } catch (error: any) {
            setMessage({type: 'error', text: error.response?.data?.detail || 'Не удалось загрузить настройки доступа'});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'АСПО.Контроль | Настройки доступа';
        if (canOpen) {
            void loadData();
        }
    }, [canOpen, loadData]);

    const handleMultiChange = (field: 'orgs' | 'deposits' | 'dns', values: HTMLCollectionOf<HTMLOptionElement>) => {
        setForm({
            ...form,
            [field]: Array.from(values).filter((item) => item.selected).map((item) => item.value),
        });
    };

    const handleRoleChange = (role: string) => {
        setForm((current) => ({
            ...current,
            role,
            orgs: isContractorRole(role) ? current.orgs : [],
            deposits: usesLocationScope(role) ? current.deposits : [],
            dns: usesLocationScope(role) ? current.dns : [],
            can_manage_access: isContractorRole(role) ? false : current.can_manage_access,
            enabled_modules: getAllowedModulesForRole(role, options),
        }));
    };

    const openEditor = useCallback((row: AccountRow) => {
        const profile = row.profile;
        setEditingProfile(profile || null);
        setForm(profile ? {
            user: profile.user,
            role: profile.role,
            orgs: profile.orgs || [],
            deposits: profile.deposits || [],
            dns: profile.dns || [],
            enabled_modules: getProfileModules(profile),
            can_upload_graph: profile.can_upload_graph,
            can_manage_access: profile.can_manage_access,
            comment: profile.comment || '',
        } : {
            ...emptyForm,
            user: row.user,
            enabled_modules: options.role_default_modules[emptyForm.role] || [],
        });
        setEditorTab('access');
        setEditorOpen(true);
    }, [getProfileModules, options.role_default_modules]);

    const closeEditor = () => {
        setEditorOpen(false);
        setEditingProfile(null);
        setForm({...emptyForm, enabled_modules: options.role_default_modules[emptyForm.role] || []});
    };

    const submitUser = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        try {
            const response = await http.post(API_SETTINGS_ACCESS_USERS, userForm);
            setUserForm(emptyUserForm);
            setCreateOpen(false);
            setMessage({type: 'success', text: 'Аккаунт создан. Теперь назначьте ему роль и доступы'});
            await loadData();
            setEditingProfile(null);
            setForm({
                ...emptyForm,
                user: response.data?.id || '',
                enabled_modules: options.role_default_modules[emptyForm.role] || [],
            });
            setEditorTab('access');
            setEditorOpen(true);
        } catch (error: any) {
            const data = error.response?.data || {};
            const text = data.detail || data.username?.[0] || data.password?.[0] || 'Не удалось создать аккаунт';
            setMessage({type: 'error', text});
        } finally {
            setLoading(false);
        }
    };

    const submitProfile = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        try {
            const payload = sanitizeAccessFormForRole(form, options);
            if (selectedAccessProfile) {
                await http.patch(`${API_SETTINGS_ACCESS_PROFILES}${selectedAccessProfile.id}/`, payload);
                setMessage({type: 'success', text: 'Настройки аккаунта сохранены'});
            } else {
                await http.post(API_SETTINGS_ACCESS_PROFILES, payload);
                setMessage({type: 'success', text: 'Профиль доступа создан'});
            }
            await loadData();
            closeEditor();
        } catch (error: any) {
            setMessage({type: 'error', text: error.response?.data?.detail || 'Не удалось сохранить профиль'});
        } finally {
            setLoading(false);
        }
    };

    const deactivateProfile = async () => {
        if (!selectedAccessProfile || !window.confirm(`Отключить профиль ${selectedAccessProfile.user_display}?`)) return;
        setLoading(true);
        try {
            await http.delete(`${API_SETTINGS_ACCESS_PROFILES}${selectedAccessProfile.id}/`);
            setMessage({type: 'success', text: 'Профиль отключен'});
            await loadData();
            closeEditor();
        } catch (error: any) {
            setMessage({type: 'error', text: error.response?.data?.detail || 'Не удалось отключить профиль'});
        } finally {
            setLoading(false);
        }
    };

    const activateProfile = async () => {
        if (!selectedAccessProfile) return;
        setLoading(true);
        try {
            await http.patch(
                `${API_SETTINGS_ACCESS_PROFILES}${selectedAccessProfile.id}/`,
                {is_active: true},
            );
            setMessage({type: 'success', text: 'Профиль активирован'});
            await loadData();
            closeEditor();
        } catch (error: any) {
            setMessage({type: 'error', text: error.response?.data?.detail || 'Не удалось активировать профиль'});
        } finally {
            setLoading(false);
        }
    };

    const toggleModule = (moduleId: string) => {
        if (!getAllowedModulesForRole(form.role, options).includes(moduleId)) {
            return;
        }
        setForm((current) => ({
            ...current,
            enabled_modules: current.enabled_modules.includes(moduleId)
                ? current.enabled_modules.filter((item) => item !== moduleId)
                : [...current.enabled_modules, moduleId],
        }));
    };

    const filterableTextColumn = useMemo<Partial<ColDef<AccountRow>>>(() => ({
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
            buttons: ['reset'],
            debounceMs: 150,
        },
    }), []);

    const autoGroupColumnDef = useMemo<ColDef<AccountRow>>(() => ({
        headerName: 'Группировка',
        width: 170,
        minWidth: 170,
        pinned: 'left',
        cellRendererParams: {
            suppressCount: false,
        },
        filter: false,
        floatingFilter: false,
        suppressMenu: true,
    }), []);

    const columnDefs = useMemo<ColDef<AccountRow>[]>(() => [
        {
            field: 'role_display',
            headerName: 'Роль',
            rowGroup: true,
            hide: true,
            filter: false,
            floatingFilter: false,
            suppressMenu: true,
        },
        {
            field: 'username',
            headerName: 'Пользователь',
            pinned: 'left',
            width: 170,
            minWidth: 170,
            ...filterableTextColumn,
            cellRenderer: (params: ICellRendererParams<AccountRow>) => params.data?.username || null,
        },
        {
            field: 'org_names',
            headerName: 'Подрядчики',
            minWidth: 220,
            filter: false,
            floatingFilter: false,
            suppressMenu: true,
            valueGetter: ({data}) => formatList(data?.org_names || []),
        },
        {
            field: 'deposit_names',
            headerName: 'Месторождения',
            width: 180,
            ...filterableTextColumn,
            valueGetter: ({data}) => formatList(data?.deposit_names || []),
        },
        {
            field: 'dns_names',
            headerName: 'ДНС',
            width: 180,
            ...filterableTextColumn,
            valueGetter: ({data}) => formatList(data?.dns_names || []),
        },
        {
            field: 'module_names',
            headerName: 'Модули',
            minWidth: 240,
            flex: 1,
            ...filterableTextColumn,
        },
        {
            field: 'rights',
            headerName: 'Права',
            width: 130,
            filter: false,
            floatingFilter: false,
            suppressMenu: true,
        },
        {
            field: 'status',
            headerName: 'Статус',
            width: 130,
            filter: false,
            floatingFilter: false,
            suppressMenu: true,
            cellClass: 'adminSettingsGridCell_center',
            cellRenderer: (params: ICellRendererParams<AccountRow>) => params.data ? (
                <S.StatusPill $kind={params.data.status_kind}>{params.data.status}</S.StatusPill>
            ) : null,
        },
        {
            headerName: '',
            width: 118,
            pinned: 'right',
            suppressMenu: true,
            sortable: false,
            filter: false,
            floatingFilter: false,
            cellClass: 'adminSettingsGridCell_center',
            cellRenderer: (params: ICellRendererParams<AccountRow>) => params.data ? (
                <S.GridActionButton type="button" onClick={() => openEditor(params.data!)}>
                    Открыть
                </S.GridActionButton>
            ) : null,
        },
    ], [filterableTextColumn, openEditor]);

    const defaultColDef = useMemo<ColDef<AccountRow>>(() => ({
        sortable: true,
        resizable: true,
        filter: false,
        floatingFilter: false,
        suppressMovable: true,
    }), []);

    if (!canOpen) {
        return (
            <S.AdminSettings>
                <S.Denied>Настройки доступа недоступны текущему пользователю</S.Denied>
            </S.AdminSettings>
        );
    }

    const selectedUser = options.users.find((item) => item.id === form.user);

    return (
        <S.AdminSettings>
            <S.Header>
                <div>
                    <span>Настройки системы</span>
                    <h1>Настройки доступа</h1>
                    <p>Аккаунты, роли, области видимости и индивидуальные модули</p>
                </div>
                <S.HeaderActions>
                    <button type="button" onClick={loadData} disabled={loading}>
                        {loading ? 'Обновляем...' : 'Обновить'}
                    </button>
                    <S.PrimaryButton type="button" onClick={() => setCreateOpen(true)} disabled={loading}>
                        Добавить аккаунт
                    </S.PrimaryButton>
                </S.HeaderActions>
            </S.Header>

            <S.Summary aria-label="Сводка по доступам">
                <div>
                    <span>Пользователей</span>
                    <strong>{accessStats.users}</strong>
                </div>
                <div>
                    <span>Активных профилей</span>
                    <strong>{accessStats.profiles}</strong>
                </div>
                <div>
                    <span>Админов</span>
                    <strong>{accessStats.admins}</strong>
                </div>
                <div>
                    <span>Подрядчиков</span>
                    <strong>{accessStats.contractors}</strong>
                </div>
                <div>
                    <span>ДНС / ОСФ / Кластер</span>
                    <strong>{accessStats.scoped}</strong>
                </div>
            </S.Summary>

            {message && (
                <S.Message $type={message.type}>
                    <span>{message.text}</span>
                    <button type="button" onClick={() => setMessage(null)}>x</button>
                </S.Message>
            )}

            <S.TablePanel>
                <S.Toolbar>
                    <label>
                        Поиск
                        <input
                            value={quickSearch}
                            onChange={(event) => setQuickSearch(event.target.value)}
                            placeholder="ФИО, логин, ДНС, подрядчик"
                        />
                    </label>
                    <label>
                        Роль
                        <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
                            <option value="">Все роли</option>
                            {options.roles.map((item) => (
                                <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Статус
                        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}>
                            <option value="all">Все</option>
                            <option value="active">Активные</option>
                            <option value="inactive">Отключенные</option>
                            <option value="empty">Без профиля</option>
                        </select>
                    </label>
                    <S.ToolbarMeta>{filteredRows.length} из {accountRows.length}</S.ToolbarMeta>
                </S.Toolbar>

                <S.GridWrap className="ag-theme-alpine">
                    <AgGridReact<AccountRow>
                        rowData={filteredRows}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        autoGroupColumnDef={autoGroupColumnDef}
                        quickFilterText={quickSearch}
                        groupDefaultExpanded={-1}
                        rowHeight={28}
                        headerHeight={28}
                        floatingFiltersHeight={28}
                        animateRows
                        getRowId={(params) => params.data.id}
                        getRowClass={(params) => {
                            if (params.node.group) return 'adminSettingsGridRow_group';
                            if (params.data?.status_kind === 'inactive') return 'adminSettingsGridRow_inactive';
                            return '';
                        }}
                        onRowDoubleClicked={(params) => params.data && openEditor(params.data)}
                    />
                </S.GridWrap>
            </S.TablePanel>

            {createOpen && (
                <S.ModalOverlay onMouseDown={() => setCreateOpen(false)}>
                    <S.ModalCard $narrow onMouseDown={(event) => event.stopPropagation()}>
                        <S.ModalHeader>
                            <div>
                                <h2>Создать аккаунт</h2>
                                <p>После создания сразу назначьте роль и доступы</p>
                            </div>
                            <button type="button" onClick={() => setCreateOpen(false)}>x</button>
                        </S.ModalHeader>

                        <S.Form onSubmit={submitUser}>
                            <label>
                                Логин
                                <input
                                    required
                                    value={userForm.username}
                                    onChange={(event) => setUserForm({...userForm, username: event.target.value})}
                                />
                            </label>
                            <label>
                                Пароль
                                <input
                                    required
                                    type="password"
                                    autoComplete="new-password"
                                    value={userForm.password}
                                    onChange={(event) => setUserForm({...userForm, password: event.target.value})}
                                />
                            </label>
                            <S.FormGrid>
                                <label>
                                    Имя
                                    <input
                                        value={userForm.first_name}
                                        onChange={(event) => setUserForm({...userForm, first_name: event.target.value})}
                                    />
                                </label>
                                <label>
                                    Фамилия
                                    <input
                                        value={userForm.last_name}
                                        onChange={(event) => setUserForm({...userForm, last_name: event.target.value})}
                                    />
                                </label>
                            </S.FormGrid>
                            <label>
                                Email
                                <input
                                    type="email"
                                    value={userForm.email}
                                    onChange={(event) => setUserForm({...userForm, email: event.target.value})}
                                />
                            </label>

                            <S.ModalFooter>
                                <button type="button" onClick={() => setCreateOpen(false)}>Отмена</button>
                                <S.PrimaryButton type="submit" disabled={loading}>Создать</S.PrimaryButton>
                            </S.ModalFooter>
                        </S.Form>
                    </S.ModalCard>
                </S.ModalOverlay>
            )}

            {editorOpen && (
                <S.ModalOverlay onMouseDown={closeEditor}>
                    <S.ModalCard onMouseDown={(event) => event.stopPropagation()}>
                        <S.ModalHeader>
                            <div>
                                <h2>{selectedAccessProfile ? selectedAccessProfile.user_display : selectedUser?.name || 'Новый профиль доступа'}</h2>
                                <p>{selectedUser?.username || selectedAccessProfile?.username || 'Назначьте профиль выбранному аккаунту'}</p>
                            </div>
                            <button type="button" onClick={closeEditor}>x</button>
                        </S.ModalHeader>

                        <S.ModalTabs>
                            <button type="button" className={editorTab === 'access' ? 'active' : ''} onClick={() => setEditorTab('access')}>Доступ</button>
                            <button type="button" className={editorTab === 'modules' ? 'active' : ''} onClick={() => setEditorTab('modules')}>Модули</button>
                            <button type="button" className={editorTab === 'requests' ? 'active' : ''} onClick={() => setEditorTab('requests')}>Заявки КС</button>
                        </S.ModalTabs>

                        <S.Form onSubmit={submitProfile}>
                            {editorTab === 'access' && (
                                <>
                                    <S.FormGrid>
                                        <label>
                                            Пользователь
                                            <select required value={form.user} onChange={(event) => {
                                                const userId = event.target.value;
                                                const profile = profiles.find((item) => item.user === userId);
                                                if (profile) {
                                                    setEditingProfile(profile);
                                                    setForm({
                                                        user: profile.user,
                                                        role: profile.role,
                                                        orgs: profile.orgs || [],
                                                        deposits: profile.deposits || [],
                                                        dns: profile.dns || [],
                                                        enabled_modules: getProfileModules(profile),
                                                        can_upload_graph: profile.can_upload_graph,
                                                        can_manage_access: profile.can_manage_access,
                                                        comment: profile.comment || '',
                                                    });
                                                } else {
                                                    setEditingProfile(null);
                                                    setForm({
                                                        ...emptyForm,
                                                        user: userId,
                                                        enabled_modules: options.role_default_modules[emptyForm.role] || [],
                                                    });
                                                }
                                            }}>
                                                <option value="">Выберите пользователя</option>
                                                {options.users.map((item) => (
                                                    <option key={item.id} value={item.id}>{item.name} / {item.username}</option>
                                                ))}
                                            </select>
                                        </label>

                                        <label>
                                            Роль
                                            <select required value={form.role} onChange={(event) => handleRoleChange(event.target.value)}>
                                                {options.roles.map((item) => (
                                                    <option key={item.id} value={item.id}>{item.name}</option>
                                                ))}
                                            </select>
                                        </label>
                                    </S.FormGrid>

                                    {isContractorRole(form.role) && (
                                        <label>
                                            Подрядчики
                                            <select multiple value={form.orgs} onChange={(event) => handleMultiChange('orgs', event.target.selectedOptions)}>
                                                {options.orgs.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                                            </select>
                                        </label>
                                    )}

                                    {usesLocationScope(form.role) && (
                                        <S.FormGrid>
                                            <label>
                                                Месторождения
                                                <select multiple value={form.deposits} onChange={(event) => handleMultiChange('deposits', event.target.selectedOptions)}>
                                                    {options.deposits.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                                                </select>
                                            </label>

                                            <label>
                                                ДНС
                                                <select multiple value={form.dns} onChange={(event) => handleMultiChange('dns', event.target.selectedOptions)}>
                                                    {options.dns.map((item) => <option key={item.id} value={item.id}>{item.label || item.name}</option>)}
                                                </select>
                                            </label>
                                        </S.FormGrid>
                                    )}

                                    <S.CheckList>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={form.can_upload_graph}
                                                onChange={(event) => setForm({...form, can_upload_graph: event.target.checked})}
                                            />
                                            Может загружать график
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={form.can_manage_access}
                                                disabled={isContractorRole(form.role)}
                                                onChange={(event) => setForm({...form, can_manage_access: event.target.checked})}
                                            />
                                            Может управлять доступами
                                        </label>
                                    </S.CheckList>

                                    <label>
                                        Комментарий
                                        <textarea value={form.comment} onChange={(event) => setForm({...form, comment: event.target.value})}/>
                                    </label>
                                </>
                            )}

                            {editorTab === 'modules' && (
                                <S.ModuleGrid>
                                    {options.modules
                                        .filter((item) => getAllowedModulesForRole(form.role, options).includes(item.id))
                                        .map((item) => (
                                            <label key={item.id}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.enabled_modules.includes(item.id)}
                                                    onChange={() => toggleModule(item.id)}
                                                />
                                                {item.name}
                                            </label>
                                        ))}
                                </S.ModuleGrid>
                            )}

                            {editorTab === 'requests' && (
                                <S.EmptyTab>
                                    <strong>Заявки КС по ДНС</strong>
                                    <span>Этот раздел подключим следующим этапом: здесь админ сможет выбрать, какие ДНС аккаунт видит, по каким создает заявки и куда получает уведомления.</span>
                                </S.EmptyTab>
                            )}

                            <S.ModalFooter>
                                {selectedAccessProfile?.is_active && (
                                    <S.DangerButton type="button" onClick={deactivateProfile} disabled={loading}>
                                        Отключить профиль
                                    </S.DangerButton>
                                )}
                                {selectedAccessProfile && !selectedAccessProfile.is_active && (
                                    <S.ActivateButton type="button" onClick={activateProfile} disabled={loading}>
                                        Активировать профиль
                                    </S.ActivateButton>
                                )}
                                <span/>
                                <button type="button" onClick={closeEditor}>Отмена</button>
                                <S.PrimaryButton type="submit" disabled={loading || !form.user}>
                                    {selectedAccessProfile ? 'Сохранить' : 'Создать профиль'}
                                </S.PrimaryButton>
                            </S.ModalFooter>
                        </S.Form>
                    </S.ModalCard>
                </S.ModalOverlay>
            )}
        </S.AdminSettings>
    );
}

function formatList(items: string[]) {
    return items?.length ? items.join(', ') : '—';
}

function formatModuleList(ids: string[], modules: ModuleOption[]) {
    const names = ids
        .map((id) => modules.find((item) => item.id === id)?.name)
        .filter(Boolean);
    return names.length ? names.join(', ') : '—';
}

export default PageAdminSettings;
